import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Card, Col, Divider, Input, Popconfirm, Row, Typography } from 'antd';
import {
    TbArrowRight,
    TbBuilding,
    TbBuildingCog,
    TbInfoCircle,
    TbLifebuoy,
    TbLogout,
    TbTrash,
    TbUpload,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { SlackSVGIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { memberService } from '@/services/database';
import { openNotionFormModal } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useMemberStore, useWorkspaceStore } from '@/stores';
import { imgLogoStyling } from '@/theme';
import { MemberPermissionType, Workspace } from '@/types';
import { formatDate, removeKeysFromLocalStorage } from '@/utilities';

import DeleteWorkspaceModal from './views/DeleteWorkspaceModal';
import UploadWorkspaceAvatarModal from './views/UploadWorkspaceAvatarModal';

// =========================
// Helpers
// =========================

const useWorkspaceActions = () => {
    const { updateWorkspace } = useWorkspaceStore();

    const update = async (id: string, payload: Partial<Workspace>) => {
        return updateWorkspace(id, payload);
    };

    const leave = async (memberId: string) => {
        return memberService.delete(memberId);
    };

    return { update, leave };
};

// =========================
// Component
// =========================

const Overview: FC = (): JSX.Element => {
    const navigate = useNavigate();
    const { token } = useAntDesignTheme();

    const { selectedMember: member } = useMemberStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();

    const { update, leave } = useWorkspaceActions();

    const [form] = Form.useForm();

    // loading states
    const [saving, setSaving] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [removingAvatar, setRemovingAvatar] = useState(false);

    // ui state
    const [avatar, setAvatar] = useState<string>('');
    const [icon, setIcon] = useState<string>('');
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [deleteWorkspaceModalVisible, setDeleteWorkspaceModalVisible] = useState(false);
    const [undoSnapshot, setUndoSnapshot] = useState<Workspace | null>(null);

    // =========================
    // Derived
    // =========================

    const canEdit = useMemo(() => {
        return member?.permission === MemberPermissionType.ADMIN || member?.permission === MemberPermissionType.OWNER;
    }, [member]);

    const isOwner = member?.permission === MemberPermissionType.OWNER;

    const hasChanges = Form.useWatch([], form);

    const isDirty = Boolean(workspace && hasChanges !== undefined && form.isFieldsTouched());

    // =========================
    // Effects
    // =========================

    useEffect(() => {
        if (!workspace) {
            return;
        }

        form.setFieldsValue({
            name: workspace.name,
            slug: workspace.subdomain,
        });

        setAvatar(workspace.avatar!);
        setIcon(workspace.emoji!);
    }, [workspace, form]);

    // =========================
    // Actions
    // =========================

    const guardOwner = () => {
        if (!isOwner) {
            toast.error('Insufficient permissions');
            return false;
        }
        return true;
    };

    const handleUpdateWorkspace = async () => {
        if (!guardOwner()) {
            return;
        }

        try {
            setSaving(true);

            const values = await form.validateFields();

            // optimistic snapshot
            setUndoSnapshot(workspace);

            await update(workspace?.id as string, {
                name: values.name,
                subdomain: values.slug,
                updatedAt: new Date(),
            });

            toast.success('Workspace Updated', {
                icon: <TbBuildingCog size='1rem' />,
                description: workspace?.updatedAt && formatDate(workspace?.updatedAt),
                action: {
                    label: 'Undo',
                    onClick: async () => {
                        if (!undoSnapshot) {
                            return;
                        }
                        await update(workspace?.id as string, undoSnapshot);
                    },
                },
            });
        } catch (error) {
            captureException(error);
            toast.error('Failed to update workspace');
        } finally {
            setSaving(false);
        }
    };

    const handleLeaveWorkspace = async () => {
        if (!member?.id) {
            return;
        }

        try {
            setLeaving(true);

            await leave(member.id);

            removeKeysFromLocalStorage(['authToken', 'refreshToken', 'activeWorkspaceId']);

            toast.success('Left workspace');
            navigate('/');
        } catch (error) {
            captureException(error);
            toast.error('Failed to leave workspace');
        } finally {
            setLeaving(false);
        }
    };

    const handleRemoveWorkspaceImage = async () => {
        if (!canEdit) {
            toast.error('Insufficient permissions');
            return;
        }

        try {
            setRemovingAvatar(true);
            await update(workspace?.id as string, { avatar: '' });
            setAvatar('');
            toast.success('Workspace image removed');
        } catch (error) {
            captureException(error);
            toast.error('Failed to remove image');
        } finally {
            setRemovingAvatar(false);
        }
    };

    // =========================
    // Render
    // =========================

    return (
        <>
            <Typography.Title id='settings-workspace-general-workspace-information' level={4}>
                Workspace Information
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Manage the identity and display information shared across this workspace.
            </Typography.Paragraph>
            <Form layout='vertical' form={form} style={{ marginBottom: 0 }} requiredMark={false}>
                {/* Avatar */}
                <Form.Item label='Avatar' style={{ marginBottom: '0.75rem' }}>
                    <Row align='middle' gutter={[12, 12]} wrap>
                        <Col flex='none'>
                            <Avatar
                                className='workspace-avatar'
                                icon={
                                    icon ? (
                                        icon
                                    ) : (
                                        <TbBuilding
                                            style={{ verticalAlign: '-0.25rem', color: token.colorTextTertiary }}
                                        />
                                    )
                                }
                                src={avatar || undefined}
                                shape='square'
                                size='large'
                                style={{
                                    cursor: canEdit ? 'pointer' : 'not-allowed',
                                    border: `1px solid ${token.colorBorder}`,
                                    borderRadius: token.borderRadius,
                                }}
                                onClick={() => canEdit && setUploadModalOpen(true)}
                            />
                        </Col>

                        <Col flex='auto' style={{ minWidth: 180 }}>
                            <small style={{ color: token.colorTextTertiary }}>
                                <TbInfoCircle style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }} />
                                JPEG or PNG, scanned before upload, 5 MB maximum.
                            </small>
                        </Col>

                        <Col flex='none'>
                            <Button
                                size='small'
                                type='dashed'
                                disabled={!canEdit}
                                onClick={() => setUploadModalOpen(true)}
                                icon={<TbUpload style={{ verticalAlign: '-0.125rem' }} />}
                            >
                                Upload
                            </Button>
                        </Col>

                        <Col flex='none'>
                            <Popconfirm
                                title='Remove workspace avatar?'
                                okText='Remove'
                                okButtonProps={{ danger: true, loading: removingAvatar }}
                                onConfirm={handleRemoveWorkspaceImage}
                                disabled={!avatar || !canEdit}
                            >
                                <Button
                                    danger
                                    size='small'
                                    type='text'
                                    disabled={!avatar || !canEdit}
                                    icon={<TbTrash style={{ verticalAlign: '-0.125rem' }} />}
                                />
                            </Popconfirm>
                        </Col>
                    </Row>
                </Form.Item>

                {/* Name */}
                <Form.Item
                    name='name'
                    label='Workspace Name'
                    rules={[
                        { required: true, message: 'Workspace name is required' },
                        { max: 120, message: 'Must be under 120 characters' },
                    ]}
                    style={{ marginBottom: '0.75rem' }}
                >
                    <Input placeholder='Acme Capital...' />
                </Form.Item>

                {/* Slug */}
                {/* <Form.Item
                    name='slug'
                    label={
                        <small style={{ color: token.colorTextTertiary }}>
                            Workspace Slug
                            <SmallTooltip
                                title={
                                    <>
                                        Lowercase letters, numbers, and dashes only. Some subdomains are reserved and
                                        may not be allowed when using a Platform Domain. We use{' '}
                                        <EnrichedAnchorTag
                                            label={'CloudFlare'}
                                            url='https://blog.cloudflare.com/access-wildcard-and-multi-hostname'
                                        />{' '}
                                        for provisioning wildcard subdomains for both Platform & Verified Domains. Learn
                                        more in <OurDocsLink />
                                    </>
                                }
                                placement='right'
                                padding='0 0.25rem'
                            >
                                <TbInfoCircle style={{ verticalAlign: '-0.1rem', marginLeft: '0.25rem' }} />
                            </SmallTooltip>
                            <PlanTag plan='enterprise' style={{ marginLeft: '0.5rem' }} />
                        </small>
                    }
                    rules={[{ required: false }, { pattern: /^[a-z0-9-]+$/, message: 'Invalid slug format' }]}
                    style={{ marginBottom: '1rem' }}
                >
                    <Row style={{ width: '100%' }} gutter={[12, 0]}>
                        <Col flex='auto'>
                            <Input placeholder='acmecapital...' disabled />
                        </Col>
                        <Col flex='none' style={{ marginRight: '-0.75rem' }}>
                            <CustomSubdomainPopover>
                                <Button style={{ padding: '0.5rem' }}>
                                    <TbWorldBolt />
                                    .countercyclical.io
                                    <TbDirection style={{ color: token.colorTextQuaternary }} />
                                </Button>
                            </CustomSubdomainPopover>
                        </Col>
                    </Row>
                </Form.Item> */}

                <Button
                    type='primary'
                    block
                    loading={saving}
                    disabled={!isDirty || !canEdit}
                    onClick={handleUpdateWorkspace}
                >
                    Save Changes
                </Button>
            </Form>

            <UploadWorkspaceAvatarModal open={uploadModalOpen} setOpen={setUploadModalOpen} />

            {/* Support */}
            <Divider style={{ margin: '1rem 0' }} />

            <Typography.Title id='settings-workspace-general-support' level={4}>
                Support
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Connect with our team for workspace guidance, troubleshooting, and platform support.
            </Typography.Paragraph>

            <Row gutter={[16, 16]} align='stretch'>
                {[
                    {
                        key: 'slack',
                        title: 'Shared Slack Channel',
                        description: 'Connect with our team in a shared Slack channel for ongoing workspace support.',
                        icon: (
                            <Avatar
                                shape='square'
                                style={{ border: `1px solid ${token.colorBorder}` }}
                                icon={
                                    <img
                                        alt='Slack'
                                        src={SlackSVGIcon}
                                        style={{ ...imgLogoStyling, transform: 'scale(0.8)' }}
                                    />
                                }
                            />
                        ),
                        action: () => openNotionFormModal('support', 'Request a Shared Slack Channel'),
                    },
                    {
                        key: 'contact',
                        title: 'Contact Support',
                        description: 'Send a support request and our team will follow up with guidance and next steps.',
                        icon: (
                            <Avatar
                                shape='square'
                                style={{ border: `1px solid ${token.colorBorder}` }}
                                icon={<TbLifebuoy size={'75%'} style={{ color: token.colorTextTertiary }} />}
                            />
                        ),
                        action: () => openNotionFormModal('support'),
                    },
                ].map((item) => (
                    <Col key={item.key} xs={24} md={12} style={{ display: 'flex' }}>
                        <Card
                            id={`settings-workspace-general-support-${item.key}`}
                            style={{ width: '100%', height: '100%' }}
                            styles={{
                                body: {
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    padding: '1rem',
                                },
                            }}
                        >
                            {item.icon}
                            <Typography.Title level={5} style={{ marginTop: '0.5rem' }}>
                                {item.title}
                            </Typography.Title>
                            <Typography.Text style={{ display: 'block', minHeight: '3rem' }} type='secondary'>
                                {item.description}
                            </Typography.Text>
                            <Button block style={{ marginTop: 'auto' }} onClick={item.action}>
                                Open <TbArrowRight />
                            </Button>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Danger Zone */}
            <Divider style={{ margin: '1.5rem 0' }} />
            <Typography.Title id='settings-workspace-general-danger-zone' level={4}>
                Danger Zone
            </Typography.Title>
            {isOwner ? (
                <>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Delete your workspace.
                    </Typography.Paragraph>
                    <Button block danger type='primary' onClick={() => setDeleteWorkspaceModalVisible(true)}>
                        <TbTrash style={{ verticalAlign: '-0.125rem' }} />
                        Delete Workspace
                    </Button>
                </>
            ) : (
                <>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Leave this workspace.
                    </Typography.Paragraph>
                    <Popconfirm
                        title='Leave workspace?'
                        description='You will lose access to all resources.'
                        okText='Leave'
                        okButtonProps={{ danger: true, loading: leaving }}
                        onConfirm={handleLeaveWorkspace}
                    >
                        <Button block danger type='dashed'>
                            Leave Workspace <TbLogout style={{ marginLeft: 6 }} />
                        </Button>
                    </Popconfirm>
                </>
            )}

            <DeleteWorkspaceModal open={deleteWorkspaceModalVisible} setOpen={setDeleteWorkspaceModalVisible} />

            {/* styles */}
            {/* <style jsx>{`
                .workspace-avatar {
                    transition: all 0.15s ease;
                }
                .workspace-avatar:hover {
                    transform: scale(1.04);
                }
            `}</style> */}
        </>
    );
};

export default Overview;
